import { ChevronDown } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { IMAGES, WHATSAPP_URL, trackWhatsAppConversion } from "../data/mediaAssets";

const Hero = () => {
    const { t } = useI18n();

    return (
        <section
            id="home"
            data-testid="hero-section"
            className="relative min-h-screen w-full flex items-center justify-center overflow-hidden"
        >
            {/* Background */}
            <img
                src={IMAGES.hero_palacio_da_pena}
                alt="Palácio da Pena — Sintra"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
            />
            {/* Overlays */}
            <div className="absolute inset-0 bg-gradient-to-b from-brand-navy-deep/70 via-brand-navy/45 to-brand-navy-deep/80" />
            <div className="absolute inset-0 bg-black/25" />

            {/* Gold accent line */}
            <div className="absolute left-6 md:left-10 top-24 md:top-28 h-32 w-px bg-brand-gold/60" />
            <div className="absolute right-6 md:right-10 bottom-24 h-32 w-px bg-brand-gold/60" />

            {/* Content */}
            <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-10 text-center">
                <p
                    data-testid="hero-eyebrow"
                    className="font-sans text-brand-gold text-[11px] md:text-xs uppercase tracking-[0.6em] mb-6 md:mb-8 animate-fade-in"
                    style={{ animationDelay: "0.1s", opacity: 0 }}
                >
                    Sintra · Cascais · Cabo da Roca · Fátima · Nazaré · Óbidos
                </p>
                <h1
                    data-testid="hero-title"
                    className="font-serif text-white text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-medium leading-[0.95] tracking-tight animate-fade-up"
                    style={{ animationDelay: "0.2s", opacity: 0 }}
                >
                    CH TOURS
                </h1>
                <div
                    className="mt-6 flex items-center justify-center gap-4 animate-fade-in"
                    style={{ animationDelay: "0.4s", opacity: 0 }}
                >
                    <span className="h-px w-12 bg-brand-gold" />
                    <span className="font-sans text-brand-gold text-xs uppercase tracking-[0.4em]">
                        Est. Portugal
                    </span>
                    <span className="h-px w-12 bg-brand-gold" />
                </div>
                <p
                    data-testid="hero-subtitle"
                    className="mt-8 md:mt-10 font-sans text-white/95 text-base md:text-xl max-w-2xl mx-auto leading-relaxed animate-fade-up"
                    style={{ animationDelay: "0.55s", opacity: 0 }}
                >
                    {t.hero.subtitle}
                </p>

                <div
                    className="mt-10 md:mt-14 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5 animate-fade-up"
                    style={{ animationDelay: "0.75s", opacity: 0 }}
                >
                    <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={trackWhatsAppConversion}
                        data-testid="hero-cta-whatsapp"
                        className="group inline-flex items-center gap-3 bg-brand-gold hover:bg-brand-gold-hover text-white font-sans text-sm md:text-[15px] uppercase tracking-[0.25em] px-8 md:px-10 py-4 md:py-5 transition-all hover:shadow-[0_20px_40px_rgba(212,175,55,0.35)]"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                        </svg>
                        <span>{t.hero.cta_whatsapp}</span>
                    </a>
                    <a
                        href="#tours"
                        data-testid="hero-cta-tours"
                        className="inline-flex items-center gap-3 border border-white/70 hover:border-brand-gold hover:text-brand-gold text-white font-sans text-sm md:text-[15px] uppercase tracking-[0.25em] px-8 md:px-10 py-4 md:py-5 backdrop-blur-sm transition-all"
                    >
                        {t.hero.cta_tours}
                    </a>
                </div>
            </div>

            {/* Scroll cue */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/70 z-10 animate-fade-in" style={{ animationDelay: "1.2s", opacity: 0 }}>
                <span className="font-sans text-[10px] uppercase tracking-[0.4em]">
                    {t.hero.scroll}
                </span>
                <ChevronDown className="w-4 h-4 animate-bounce" />
            </div>
        </section>
    );
};

export default Hero;
